/**
 * A201 — a minimal PDF writer for the order sheet: each page is one JPEG drawn over the whole A4 page (what the
 * try-on drew on a canvas, so every typeface and symbol of the engraving is exactly as the customer saw it).
 * No library, no network: built on the device, like the photo.
 *
 * Structure: catalog, page tree, one page per image (its content stream draws the image full page), the
 * images as DCT (JPEG) XObjects, an Info dictionary (title, subject, author, creation date) and the
 * cross-reference table with exact byte offsets.
 */
export interface PdfPage {
  /** JPEG file bytes (baseline or progressive, RGB). */
  jpeg: Uint8Array;
  /** Pixel size of the JPEG. */
  width: number;
  height: number;
}

export interface PdfMeta {
  title: string;
  subject?: string;
  author?: string;
  date?: Date;
}

/** A4 portrait in points (1/72 in). */
export const A4 = { width: 595.28, height: 841.89 } as const;

const encoder = new TextEncoder();

/** A text string for the Info dictionary: ASCII as a literal, anything else as UTF-16BE with its BOM. */
export function pdfString(text: string): string {
  if (/^[\x20-\x7e]*$/.test(text)) return `(${text.replace(/[\\()]/g, m => `\\${m}`)})`;
  let hex = 'FEFF';
  for (let i = 0; i < text.length; i++) hex += text.charCodeAt(i).toString(16).padStart(4, '0').toUpperCase();
  return `<${hex}>`;
}

function pdfDate(d: Date): string {
  const two = (n: number) => String(n).padStart(2, '0');
  return `(D:${d.getUTCFullYear()}${two(d.getUTCMonth() + 1)}${two(d.getUTCDate())}${two(d.getUTCHours())}${two(d.getUTCMinutes())}${two(d.getUTCSeconds())}Z)`;
}

/** The PDF file bytes. */
export function jpegPdf(pages: readonly PdfPage[], meta: PdfMeta): Uint8Array {
  const chunks: Uint8Array[] = [];
  let length = 0;
  const offsets: number[] = [];
  const push = (part: string | Uint8Array) => {
    const bytes = typeof part === 'string' ? encoder.encode(part) : part;
    chunks.push(bytes);
    length += bytes.length;
  };
  // Object numbers: 1 catalog, 2 pages, 3 info, then per page: page, content, image.
  const pageObj = (i: number) => 4 + i * 3;
  const count = 3 + pages.length * 3;
  const begin = (n: number) => { offsets[n] = length; push(`${n} 0 obj\n`); };
  const end = () => push('\nendobj\n');

  // Header; the binary comment line tells transfer tools the file is binary.
  push('%PDF-1.4\n');
  push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

  begin(1); push('<< /Type /Catalog /Pages 2 0 R >>'); end();
  begin(2); push(`<< /Type /Pages /Count ${pages.length} /Kids [${pages.map((_, i) => `${pageObj(i)} 0 R`).join(' ')}] >>`); end();
  const info = [`/Title ${pdfString(meta.title)}`, `/Producer ${pdfString('Maison Mata virtual try-on')}`, `/CreationDate ${pdfDate(meta.date ?? new Date())}`];
  if (meta.subject) info.push(`/Subject ${pdfString(meta.subject)}`);
  if (meta.author) info.push(`/Author ${pdfString(meta.author)}`);
  begin(3); push(`<< ${info.join(' ')} >>`); end();

  pages.forEach((page, i) => {
    const n = pageObj(i);
    const W = A4.width.toFixed(2), H = A4.height.toFixed(2);
    begin(n);
    push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /XObject << /Im${i} ${n + 2} 0 R >> /ProcSet [/PDF /ImageC] >> /Contents ${n + 1} 0 R >>`);
    end();
    const content = `q ${W} 0 0 ${H} 0 0 cm /Im${i} Do Q`;
    begin(n + 1); push(`<< /Length ${encoder.encode(content).length} >>\nstream\n${content}\nendstream`); end();
    begin(n + 2);
    push(`<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.jpeg.length} >>\nstream\n`);
    push(page.jpeg);
    push('\nendstream');
    end();
  });

  const xref = length;
  let table = `xref\n0 ${count + 1}\n0000000000 65535 f \n`;
  for (let n = 1; n <= count; n++) table += `${String(offsets[n] ?? 0).padStart(10, '0')} 00000 n \n`;
  push(table);
  push(`trailer\n<< /Size ${count + 1} /Root 1 0 R /Info 3 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

  const out = new Uint8Array(length);
  let at = 0;
  for (const c of chunks) { out.set(c, at); at += c.length; }
  return out;
}
